import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { HttpService, NotificationService } from '../../../tms-shared';
import { TmsWebBaseEditCreateService } from '../base';
import {
  DeliveryDate,
  OrderLineStatusCheck,
  PurchaseOrderAllocations,
  PurchaseOrderFromPreviousIds,
  PurchaseOrderLine,
  PurchaseOrderLineCheckStatus,
  PurchaseOrderLineDetails,
  PurchaseOrderLineForAllocating,
  PurchaseOrderLineForCompleting,
  PurchaseOrderLineHeader,
  PurchaseOrderLinePreviousOrders,
  PurchaseOrderLinePurchasesAllocationsSummaryTop,
  PurchaseOrderLineQuickCreate,
  PurchaseOrderLineReceipt,
  PurchaseOrderLineRelocate,
  PurchaseOrderLineRequirementsSummaryTop,
  PurchaseOrderLineSalesAllocationsSummaryTop,
  PurchaseOrderMultiProductCreate,
  PurchaseOrderPurchaseAllocation,
  PurchasePriceFromPriceListRequest,
  PurchasePriceFromPriceListResponse,
  SalesOrderLineAllocationsSummaryTop,
  SalesOrderPurchaseAllocation,
  StockEnquiryPurchaseOrder,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderLineService extends TmsWebBaseEditCreateService<PurchaseOrderLine, PurchaseOrderLineDetails> {
  public refreshDetails = new Subject<void>();

  constructor(
    protected httpService: HttpService,
    protected notificationService: NotificationService,
  ) {
    super('PurchaseOrderLine', $localize`Purchase Order Line`, httpService, notificationService);
  }

  getForPurchaseOrderHeader(id: string): Observable<PurchaseOrderLineHeader[]> {
    return this.httpService.get<PurchaseOrderLineHeader[]>(`${this.apiUrl}/PurchaseOrderHeader?id=${id}`);
  }

  requirementsSummaryTop(id: string): Observable<PurchaseOrderLineRequirementsSummaryTop> {
    return this.httpService.get<PurchaseOrderLineRequirementsSummaryTop>(
      `${this.apiUrl}/PurchaseOrderLineRequirementsSummaryTop?id=${id}`,
    );
  }

  getForPurchaseOrderReceipt(id: string): Observable<PurchaseOrderLineReceipt> {
    return this.httpService.get<PurchaseOrderLineReceipt>(`${this.apiUrl}/PurchaseOrderLineReceipt?id=${id}`);
  }

  purchaseOrderPurchaseAllocation(id: string): Observable<PurchaseOrderPurchaseAllocation[]> {
    return this.httpService.get<PurchaseOrderPurchaseAllocation[]>(
      `${this.apiUrl}/PurchaseOrderPurchaseAllocation?id=${id}`,
    );
  }

  getPreviousPurchaseOrders(
    purchaseOrderHeaderId: string,
    search: string,
  ): Observable<PurchaseOrderLinePreviousOrders[]> {
    return this.httpService.get<PurchaseOrderLinePreviousOrders[]>(
      `${this.apiUrl}/PreviousPurchaseOrders?purchaseOrderHeaderId=${purchaseOrderHeaderId}&search=${search}`,
    );
  }

  getCheckStatus(id: string): Observable<PurchaseOrderLineCheckStatus> {
    return this.httpService.get<PurchaseOrderLineCheckStatus>(`${this.apiUrl}/CheckStatus?id=${id}`);
  }

  getCheckStatusChangeAllowed(purchaseOrderLineId: string, newStatusId: string): Observable<OrderLineStatusCheck> {
    return this.httpService.get<OrderLineStatusCheck>(
      `${this.apiUrl}/CheckStatusChangeAllowed?purchaseOrderLineId=${purchaseOrderLineId ?? ''}&newStatusID=${newStatusId}`,
    );
  }

  purchaseOrderAllocations(id: string, getUnAllocated: boolean): Observable<PurchaseOrderAllocations> {
    return this.httpService.get<PurchaseOrderAllocations>(
      `${this.apiUrl}/PurchaseOrderAllocation?id=${id}&getUnAllocated=${getUnAllocated}`,
    );
  }

  getUnAllocatedPurchaseOrders(productId: string): Observable<StockEnquiryPurchaseOrder[]> {
    return this.httpService.get<StockEnquiryPurchaseOrder[]>(
      `${this.apiUrl}/GetUnAllocatedPurchaseOrders?productId=${productId}`,
    );
  }

  salesOrderPurchaseAllocation(id: string, includeComplete: boolean): Observable<SalesOrderPurchaseAllocation[]> {
    return this.httpService.get<SalesOrderPurchaseAllocation[]>(
      `${this.apiUrl}/SalesOrderPurchaseAllocation?id=${id}&includeComplete=${includeComplete}`,
    );
  }

  getPurchaseOrdersForAllocating(
    productId: string,
    purchaseOrderLineId: string,
  ): Observable<PurchaseOrderLineForAllocating[]> {
    return this.httpService.get<PurchaseOrderLineForAllocating[]>(
      `${this.apiUrl}/GetPurchaseOrdersForAllocating?productId=${productId}&purchaseOrderLineId=${purchaseOrderLineId}`,
    );
  }

  getOutstandingOrderLines(): Observable<PurchaseOrderLineForCompleting[]> {
    return this.httpService.get<PurchaseOrderLineForCompleting[]>(`${this.apiUrl}/PurchaseOrderLinesForCompleting`);
  }

  checkForAllocations(id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckForAllocations?id=${id}`);
  }

  callOffStock(id: string): Observable<PurchaseOrderLineRelocate> {
    return this.httpService.get<PurchaseOrderLineRelocate>(`${this.apiUrl}/CallOffStock?id=${id}`);
  }

  quickCreate(PurchaseOrderLineQuickCreate: PurchaseOrderLineQuickCreate): Observable<PurchaseOrderLineQuickCreate> {
    return this.httpService.post(`${this.apiUrl}/QuickCreate`, PurchaseOrderLineQuickCreate);
  }

  deliveryDate(model: DeliveryDate): Observable<DeliveryDate> {
    return this.httpService.post<DeliveryDate>(`${this.apiUrl}/DeliveryDate`, model);
  }

  getPriceFromPriceList(model: PurchasePriceFromPriceListRequest): Observable<PurchasePriceFromPriceListResponse> {
    return this.httpService.post<PurchasePriceFromPriceListResponse>(`${this.apiUrl}/PriceFromPriceList`, model);
  }

  salesAllocationsSummaryTop(id: string): Observable<PurchaseOrderLineSalesAllocationsSummaryTop> {
    return this.httpService.get<PurchaseOrderLineSalesAllocationsSummaryTop>(
      `${this.apiUrl}/SalesAllocationsSummaryTop?id=${id}`,
    );
  }

  purchaseAllocationsSummaryTop(id: string): Observable<PurchaseOrderLinePurchasesAllocationsSummaryTop> {
    return this.httpService.get<PurchaseOrderLinePurchasesAllocationsSummaryTop>(
      `${this.apiUrl}/PurchaseAllocationsSummaryTop?id=${id}`,
    );
  }
  swapStockPurchaseAllocationsSummaryTop(id: string): Observable<SalesOrderLineAllocationsSummaryTop> {
    return this.httpService.get<SalesOrderLineAllocationsSummaryTop>(
      `${this.apiUrl}/SwapStockPurchaseAllocationsSummaryTop?id=${id}`,
    );
  }

  createFromPreviousOrders(
    purchaseOrderHeaderId: string,
    ids: PurchaseOrderFromPreviousIds[],
  ): Observable<PurchaseOrderLineQuickCreate> {
    return this.httpService.post<PurchaseOrderLineQuickCreate>(`${this.apiUrl}/CreateFromPreviousOrders`, {
      purchaseOrderHeaderId,
      ids,
    });
  }

  completeOrderLines(ids: string[]): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/CompleteOrderLines`, { ids });
  }

  amendOrder(model: PurchaseOrderLineHeader): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/AmendOrder`, model);
  }

  updateCallOff(model: PurchaseOrderLineHeader): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/UpdateCallOff`, model);
  }

  createMultiProductOrderLines(
    purchaseOrderHeaderId: string,
    purchaseOrderMultiProducts: PurchaseOrderMultiProductCreate[],
  ) {
    return this.httpService.post(`${this.apiUrl}/CreateMultiProductOrderLines`, {
      purchaseOrderHeaderId,
      purchaseOrderMultiProducts,
    });
  }
}
